import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import Modal from '../../modal/Modal';
import loader from '../../assets/loader.png';
import { Option } from '../../types/option';

export interface IACible {
    id: number;
    name: string;
}

export interface IASubject {
    id: number;
    subject: string;
}

export type IATextGenerationOptions = {
    userSso: boolean;
    userId: Option<number>;
    isAdminForIa: boolean;
    onGeneratedText: (text: string) => void;
};

type IaTone = { tone: string };

type UserIaInfo = { requestIA: number; quotaIA: number };

export function useIATextGeneration({
    userSso,
    userId,
    isAdminForIa,
    onGeneratedText,
}: IATextGenerationOptions) {
    const [isModalIAOpen, setModalIAOpen] = useState(false);
    const [tone, setTone] = useState<IaTone[]>([]);
    const [selectedTone, setSelectedTone] = useState<string | null>(null);
    const [cibles, setCibles] = useState<IACible[]>([]);
    const [selectedCible, setSelectedCible] = useState<IACible | null>(null);
    const [subjects, setSubjects] = useState<IASubject[]>([]);
    const [selectedSubject, setSelectedSubject] = useState<IASubject | null>(null);
    const [isLoadingIaModal, setIsLoadingIaModal] = useState(false);
    const [userQuotaReached, setUserQuotaReached] = useState(false);
    const [isErrorVisible, setIsErrorVisible] = useState(false);

    useEffect(() => {
        if (userQuotaReached) {
            setIsErrorVisible(true);
            const timer = setTimeout(() => {
                setIsErrorVisible(false);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [userQuotaReached]);

    useEffect(() => {
        const fetchCibles = async () => {
            try {
                const cibleResp = await axios.get('/api/ia_cibles');
                setCibles(cibleResp?.data['hydra:member'] ?? []);
            } catch (error) {
                console.error('Error when fetching cibles: ', error);
            }
        };
        void fetchCibles();
    }, []);

    useEffect(() => {
        if (selectedCible) {
            const fetchSubjects = async () => {
                try {
                    const subjectResp = await axios.get(
                        `/api/ia_subjects?cible=${selectedCible.id}`,
                    );
                    setSubjects(subjectResp?.data['hydra:member'] ?? []);
                    setSelectedSubject(null);
                } catch (error) {
                    console.error('Error fetching subjects: ', error);
                }
            };
            void fetchSubjects();
        }
    }, [selectedCible]);

    const getCurrentUserRequest = useCallback(
        async (uid: number): Promise<UserIaInfo> => {
            const userUrl = userSso ? `/api/users/${uid}` : `/api/user_admins/${uid}`;
            const response = await axios.get(userUrl);
            return response.data;
        },
        [userSso],
    );

    const handleCloseModalIA = useCallback(() => {
        setSelectedCible(null);
        setSelectedSubject(null);
        setSelectedTone(null);
        setModalIAOpen(false);
    }, []);

    const handleOpenIAModal = useCallback(() => setModalIAOpen(true), []);

    const openIAModal = useCallback(
        async (event: React.MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            setUserQuotaReached(false);
            userId.map(async id => {
                const currentUser = await getCurrentUserRequest(id);
                if (currentUser.requestIA < currentUser.quotaIA) {
                    try {
                        const toneResp = await axios.get('/api/ia_tones');
                        setTone(toneResp?.data['hydra:member'] ?? []);
                    } catch (error) {
                        console.error(error);
                    }
                    handleOpenIAModal();
                } else {
                    setUserQuotaReached(true);
                }
            });
        },
        [getCurrentUserRequest, handleOpenIAModal, userId],
    );

    const closeIaGeneration = () => setIsLoadingIaModal(false);

    const handleCibleChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedN = parseInt(event.target.value, 10);
            if (Number.isNaN(selectedN)) {
                setSelectedCible(null);
                return;
            }
            setSelectedCible(cibles.find(c => c.id === selectedN) || null);
        },
        [cibles],
    );

    const handleSubjectChange = useCallback(
        (event: React.ChangeEvent<HTMLSelectElement>) => {
            const selectedN = parseInt(event.target.value, 10);
            if (Number.isNaN(selectedN)) {
                setSelectedSubject(null);
                return;
            }
            setSelectedSubject(subjects.find(s => s.id === selectedN) || null);
        },
        [subjects],
    );

    const handleToneChange = (event: React.ChangeEvent<HTMLSelectElement>) =>
        setSelectedTone(event.target.value);

    const handleTextGeneration = useCallback(async () => {
        if (!selectedCible || !selectedSubject || !selectedTone) {
            console.error('Please select a cible, subject, and tone.');
            return;
        }

        userId.map(async id => {
            onGeneratedText('');

            const payload = {
                cible: selectedCible.name,
                subject: selectedSubject.subject,
                tone: selectedTone,
                user: id,
                isAdmin: isAdminForIa,
            };

            handleCloseModalIA();
            setIsLoadingIaModal(true);
            try {
                const currentUser = await getCurrentUserRequest(id);
                if (currentUser.requestIA < currentUser.quotaIA) {
                    const url = userSso ? `/api/users/${id}` : `/api/user_admins/${id}`;
                    await axios.patch(
                        url,
                        { requestIA: currentUser.requestIA + 1 },
                        { headers: { 'Content-Type': 'application/merge-patch+json' } },
                    );
                }
            } catch (error) {
                console.error('Error when update requestIa ', error);
            }

            const response = await axios({
                method: 'POST',
                url: '/api/generate-text',
                data: payload,
            });
            try {
                onGeneratedText(response.data.generated_text);
                setIsLoadingIaModal(false);
            } catch (error) {
                console.error('Error submitting generate-text:', error);
                handleCloseModalIA();
            }
        });
    }, [
        selectedCible,
        selectedSubject,
        selectedTone,
        userId,
        onGeneratedText,
        isAdminForIa,
        handleCloseModalIA,
        getCurrentUserRequest,
        userSso,
    ]);

    const iaTextModals = (
        <>
            <Modal isOpen={isModalIAOpen} onClose={handleCloseModalIA}>
                <span className="text-heavy font-super-heavy">Générer un texte via l'IA</span>
                <label htmlFor={'cible'} className={'text-[12px] pt-[10px] mr-auto font-light'}>
                    Votre cible :
                </label>
                <select name="cible" id={'cible'} onChange={handleCibleChange}>
                    <option value="">Choisir votre cible</option>
                    {cibles.map(c => (
                        <option key={c.id} value={c.id}>
                            {c.name}
                        </option>
                    ))}
                </select>
                {selectedCible && (
                    <>
                        <label
                            htmlFor={'subject'}
                            className={'text-[12px] pt-[10px] mr-auto font-light'}
                        >
                            Le sujet :
                        </label>
                        <select
                            name="sujet"
                            id={'subject'}
                            onChange={handleSubjectChange}
                            value={selectedSubject?.id || ''}
                        >
                            <option value="">Choisir votre sujet</option>
                            {subjects.map(s => (
                                <option key={s.id} value={s.id}>
                                    {s.subject}
                                </option>
                            ))}
                        </select>
                    </>
                )}
                {selectedCible && selectedSubject && (
                    <>
                        <label
                            htmlFor={'tone'}
                            className={'text-[12px] pt-[10px] mr-auto font-light'}
                        >
                            Ton de la publication :
                        </label>
                        <select
                            name="tone"
                            id={'tone'}
                            onChange={handleToneChange}
                            value={selectedTone || ''}
                        >
                            <option value={''}>Choisir le ton</option>
                            {tone.map((t, index) => (
                                <option key={index} value={t.tone}>
                                    {t.tone}
                                </option>
                            ))}
                        </select>
                    </>
                )}
                <div className="flex w-full justify-around pt-[30px]">
                    <button
                        type="button"
                        onClick={handleCloseModalIA}
                        className={
                            'block text-black px-4 py-2 text-small leading-[1.5] rounded-[1.5rem] justify-center items-center w-[200px] font-medium bg-white mb-[10px] border border-light-gray'
                        }
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={() => void handleTextGeneration()}
                        className={
                            'block text-black px-4 py-2 text-small leading-[1.5] rounded-[1.5rem] justify-center items-center w-[200px] font-medium mb-[10px] bg-primary border border-light-gray'
                        }
                    >
                        Générer le texte
                    </button>
                </div>
            </Modal>
            <Modal isOpen={isLoadingIaModal} onClose={closeIaGeneration}>
                <span className="text-heavy font-super-heavy">Générer un texte via l'IA</span>
                <img src={loader} alt="loader" className={'mt-[15px] animate-spin'} />
                <p className="mt-4 mb-2">Génération en cours ...</p>
            </Modal>
        </>
    );

    return { openIAModal, isErrorVisible, iaTextModals };
}
